import { Map } from "immutable";

import { ItemDatesRecord } from "../../../types/immutableRecords/display/shared/ItemDatesRecord";
import { keySetFromMap } from "../../../utils/immutableExtensions";
import { definitionSelectors } from "../../definition";
import { calendarSelectors } from "../../definition/calendar";
import { itemSelectors } from "../../items";
import { superAndChildrenSelectors } from "../../superAndChildrenById";
import updateItemDisplayDates from "./updateItemDisplayDates";

const insertNewItems = (state, newIds) => {
  return state.withMutations(s => {
    newIds.forEach(cId => {
      s.set(cId, ItemDatesRecord());
    });
  });
};

const reducer = (state = Map(), action, newGlobalState, prevGlobalState) => {
  // If we have no data loaded yet, don't bother deriving anything
  if (!newGlobalState.isDataLoaded) {
    return state;
  }

  const todayDate = newGlobalState.display.lastTodayDate;

  // Grab some stuff we will need
  const newItemsById = itemSelectors.getItemsById(newGlobalState);
  const prevItemsById = itemSelectors.getItemsById(prevGlobalState);

  const newSuperAndChildrenById = superAndChildrenSelectors.getSuperAndChildrenById(newGlobalState);
  const prevSuperAndChildrenById = superAndChildrenSelectors.getSuperAndChildrenById(prevGlobalState);

  const newCalendar = calendarSelectors.getCalendar(newGlobalState);
  const prevCalendar = calendarSelectors.getCalendar(prevGlobalState);

  const isDataChanged = newItemsById !== prevItemsById || newSuperAndChildrenById !== prevSuperAndChildrenById;

  // Other changes of interest
  const isDefinitionsChanged =
    definitionSelectors.getEnabledDefinitions(newGlobalState) !==
      definitionSelectors.getEnabledDefinitions(prevGlobalState) || newCalendar !== prevCalendar;

  if (!isDataChanged && !isDefinitionsChanged) {
    return state;
  }

  let newState = state;
  // Note: This one is a cached selected, so we are calling previous before new in the hope that it will avoid cache
  // thrashing.
  const prevDescendantsById = superAndChildrenSelectors.getDescendantsMapFromSuperAndChildren(prevSuperAndChildrenById);
  const newDescendantsById = superAndChildrenSelectors.getDescendantsMapFromSuperAndChildren(newSuperAndChildrenById);

  // Note: This one is a cached selected, so we are calling previous before new in the hope that it will avoid cache
  // thrashing.
  const prevAncestorsById = superAndChildrenSelectors.getAncestorsMapFromSuperAndChildren(prevSuperAndChildrenById);
  const newAncestorsById = superAndChildrenSelectors.getAncestorsMapFromSuperAndChildren(newSuperAndChildrenById);

  const newAndChangedIdSet = keySetFromMap(newItemsById.filter((item, id) => item !== prevItemsById.get(id)));
  const newIds = keySetFromMap(newItemsById.filter((item, id) => !prevItemsById.has(id)));
  const deletedIds = keySetFromMap(prevItemsById.filter((item, id) => !newItemsById.has(id)));

  // Remove any that should not exist anymore!
  if (deletedIds.size) {
    newState = newState.deleteAll(deletedIds);
  }

  // Create items for anything new, so that they already exist when we go to update them in the next section
  if (newIds.size) {
    newState = insertNewItems(newState, newIds);
  }

  // Update derived item dates
  newState = updateItemDisplayDates(
    newState,
    action,
    newAndChangedIdSet,
    todayDate,
    newItemsById,
    prevItemsById,
    prevAncestorsById,
    newAncestorsById,
    newDescendantsById,
    prevDescendantsById,
    newCalendar,
    prevCalendar
  );

  return newState;
};

export default reducer;
